import { useState, useEffect, lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './components/providers/ThemeProvider';
import { CompanyProvider, useCompanyContext } from './contexts/CompanyContext';
import { useAuth } from './hooks/useAuth';
import { useCompanies } from './hooks/useCompanies';
import { LoadingScreen } from './components/primitives/LoadingScreen';
import { ErrorBoundary } from './components/primitives/ErrorBoundary';
import { AppLayout } from './components/layout/AppLayout';
import { queryClient } from './lib/react-query';
import { SkipToMain } from './lib/accessibility.tsx';

// Import skeletons directly (not lazy) for instant display
import { GenericPageSkeleton } from './components/primitives';
import { SharingSkeleton } from './features/sharing/components/SharingSkeleton';
import { SettingsSkeleton } from './features/settings';

// OAuth integration
import OAuthLoginPage from './features/oauth/OAuthLoginPage';

// ===============================================
// EAGER LOADING - Primary Routes (80% usage)
// Loaded immediately for instant navigation
// ===============================================
import { DashboardPage, DashboardSkeleton } from './features/dashboard';
import { TransactionsPage, TransactionFormPage, TransactionListSkeleton, TransactionFormSkeleton } from './features/transactions';
import { AuthPage } from './features/auth';
import { CompanySelector } from './features/companies';
import { DatabaseDiagnostic } from './components/DatabaseDiagnostic';
import { ErrorDebugPanel } from './components/debug/ErrorDebugPanel';
import { RestrictedRoute } from './components/permissions/RestrictedRoute';

// ===============================================
// LAZY LOADING - Secondary Routes (20% usage)
// Code-split for smaller initial bundle
// ===============================================
const ReportsPage = lazy(() => import('./features/reports').then(m => ({ default: m.ReportsPage })));
const BudgetPage = lazy(() => import('./features/budgets/BudgetPageNew').then(m => ({ default: m.BudgetPageNew })));
const ScenariosPage = lazy(() => import('./features/scenarios').then(m => ({ default: m.ScenariosPage })));
const ScenarioFormPage = lazy(() => import('./features/scenarios').then(m => ({ default: m.ScenarioFormPage })));
const SharingPage = lazy(() => import('./features/sharing').then(m => ({ default: m.SharingPage })));
const InvitePage = lazy(() => import('./features/sharing').then(m => ({ default: m.InvitePage })));
const SettingsPage = lazy(() => import('./features/settings').then(m => ({ default: m.SettingsPage })));
const ConfigurationsPage = lazy(() => import('./features/configurations/ConfigurationsPage'));
const ReconciliationPage = lazy(() => import('./features/bank-reconciliation').then(m => ({ default: m.ReconciliationPage })));

type Page = 'dashboard' | 'entries' | 'entries-form' | 'reports' | 'budget' | 'scenarios' | 'scenarios-form' | 'sharing' | 'settings' | 'configurations' | 'reconciliation' | 'diagnostic';

function AppContent() {
  const { user, loading: authLoading, signIn, signUp, signOut } = useAuth();
  const { companies, loading: companiesLoading, createCompany } = useCompanies(user?.id);
  const { setAllCompanies, hasSelection } = useCompanyContext();
  
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [editingTransactionId, setEditingTransactionId] = useState<string | undefined>();
  const [transactionFormMode, setTransactionFormMode] = useState<'new' | 'edit' | 'recurring' | 'duplicate'>('new');
  const [duplicateFromId, setDuplicateFromId] = useState<string | undefined>();
  const [editingScenarioId, setEditingScenarioId] = useState<string | undefined>();
  const [showInvitePage, setShowInvitePage] = useState(false);
  const [showOAuthPage, setShowOAuthPage] = useState(false);

  // Check for OAuth parameters in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasOAuthParams = searchParams.has('state') && searchParams.has('redirect_uri');
    setShowOAuthPage(hasOAuthParams);
  }, []);

  // Check for invite code in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasCode = searchParams.has('code');
    setShowInvitePage(hasCode);
  }, []);

  // Sync companies to CompanyContext
  useEffect(() => {
    if (companies.length > 0) {
      setAllCompanies(companies);
    }
  }, [companies, setAllCompanies]);

  // Intelligent preloading - Load secondary pages after 2s idle
  useEffect(() => {
    if (!user || !hasSelection) return;

    const preloadTimer = setTimeout(() => {
      // Preload secondary routes in background (low priority)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          import('./features/reports');
          import('./features/budget');
          import('./features/scenarios');
          import('./features/sharing');
          import('./features/settings');
        }, { timeout: 5000 });
      } else {
        // Fallback for Safari
        setTimeout(() => {
          import('./features/reports');
          import('./features/budgets/BudgetPageNew');
          import('./features/scenarios');
          import('./features/sharing');
          import('./features/settings');
        }, 100);
      }
    }, 2000); // Start preloading after 2s of user being logged in

    return () => clearTimeout(preloadTimer);
  }, [user, hasSelection]);

  const handleNavigate = (path: string) => {
    console.log('[App] handleNavigate called with:', path);
    
    // Separar path e query params
    const [basePath, queryString] = path.split('?');
    
    console.log('[App] Split:', { basePath, queryString });
    
    const pageMap: Record<string, Page> = {
      '/dashboard': 'dashboard',
      '/entries': 'entries',
      '/reports': 'reports',
      '/budget': 'budget',
      '/scenarios': 'scenarios',
      '/sharing': 'sharing',
      '/configurations': 'configurations',
      '/reconciliation': 'reconciliation',
      '/settings': 'settings',
      '/diagnostic': 'diagnostic',
    };
    
    const page = pageMap[basePath];
    console.log('[App] Mapped page:', page);
    
    if (page) {
      // Atualizar URL do navegador para manter query params
      if (queryString) {
        const newURL = `${window.location.pathname}${window.location.search}`;
        window.history.pushState({}, '', `${basePath}?${queryString}`);
        console.log('[App] Updated URL with query params:', `${basePath}?${queryString}`);
      }
      
      setCurrentPage(page);
      // Clear transaction editing state when navigating away from form
      if (page !== 'entries-form') {
        setEditingTransactionId(undefined);
      }
    }
  };

  const handleNavigateToTransactionForm = (
    transactionId?: string, 
    options?: { mode?: 'recurring' | 'duplicate'; duplicateFromId?: string }
  ) => {
    if (options?.mode === 'recurring') {
      setTransactionFormMode('recurring');
      setEditingTransactionId(undefined);
      setDuplicateFromId(undefined);
    } else if (options?.mode === 'duplicate' && options?.duplicateFromId) {
      setTransactionFormMode('duplicate');
      setDuplicateFromId(options.duplicateFromId);
      setEditingTransactionId(undefined);
    } else if (transactionId) {
      setTransactionFormMode('edit');
      setEditingTransactionId(transactionId);
      setDuplicateFromId(undefined);
    } else {
      setTransactionFormMode('new');
      setEditingTransactionId(undefined);
      setDuplicateFromId(undefined);
    }
    setCurrentPage('entries-form');
  };

  const handleBackFromTransactionForm = () => {
    setEditingTransactionId(undefined);
    setTransactionFormMode('new');
    setDuplicateFromId(undefined);
    setCurrentPage('entries');
  };

  const handleNavigateToScenarioForm = (scenarioId?: string) => {
    setEditingScenarioId(scenarioId);
    setCurrentPage('scenarios-form');
  };

  const handleBackFromScenarioForm = () => {
    setEditingScenarioId(undefined);
    setCurrentPage('scenarios');
  };

  const handleSignOut = async () => {
    await signOut();
    setCurrentPage('dashboard');
  };

  const getBreadcrumbs = () => {
    const breadcrumbMap: Record<Page, Array<{ label: string; href?: string }>> = {
      dashboard: [{ label: 'Dashboard' }],
      entries: [{ label: 'Lançamentos' }],
      'entries-form': [
        { label: 'Lançamentos', href: '/entries' },
        { label: editingTransactionId ? 'Editar' : 'Novo' }
      ],
      reports: [{ label: 'Relatórios' }],
      budget: [{ label: 'Orçamento' }],
      scenarios: [{ label: 'Cenários' }],
      'scenarios-form': [
        { label: 'Cenários', href: '/scenarios' },
        { label: editingScenarioId ? 'Editar' : 'Novo' }
      ],
      sharing: [{ label: 'Compartilhamento' }],
      settings: [{ label: 'Configurações' }],
      configurations: [{ label: 'Dados Mestres' }],
      diagnostic: [{ label: 'Diagnóstico do Banco' }],
    };

    return breadcrumbMap[currentPage] || [];
  };

  // OAuth authorization flow - Show OAuth page (works with or without authentication)
  if (showOAuthPage) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <SkipToMain />
          <OAuthLoginPage />
          <Toaster />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Loading state
  if (authLoading) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <div className="size-full flex items-center justify-center" data-testid="app-loading">
            <LoadingScreen message="Carregando..." />
          </div>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Not authenticated with invite code - Show InvitePage
  if (!user && showInvitePage) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <SkipToMain />
            <Suspense fallback={<LoadingScreen message="Carregando..." />}>
              <InvitePage onSuccess={() => {
                setShowInvitePage(false);
                window.location.reload();
              }} />
            </Suspense>
            <Toaster />
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  // Not authenticated - No Suspense needed (eager loaded)
  if (!user) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <SkipToMain />
          <AuthPage onSignIn={signIn} onSignUp={signUp} />
          <Toaster />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Authenticated with invite code - Show InvitePage
  if (user && showInvitePage) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <SkipToMain />
            <Suspense fallback={<LoadingScreen message="Processando convite..." />}>
              <InvitePage onSuccess={() => {
                setShowInvitePage(false);
                setCurrentPage('dashboard');
              }} />
            </Suspense>
            <Toaster />
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  // No company available - Show company selector
  if (companies.length === 0 && !companiesLoading) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <SkipToMain />
          <CompanySelector
            companies={companies}
            loading={companiesLoading}
            onSelect={() => {}} // Not used anymore - Context handles it
            onCreateCompany={createCompany}
          />
          <Toaster />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
  
  // Loading companies
  if (companiesLoading) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <div className="size-full flex items-center justify-center">
            <LoadingScreen message="Carregando empresas..." />
          </div>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Render page content
  const renderPage = () => {
    switch (currentPage) {
      // Primary routes - No Suspense needed (eager loaded, instant navigation)
      case 'dashboard':
        return (
          <DashboardPage
            userId={user.id}
            onNavigateToTransactionForm={handleNavigateToTransactionForm}
            onNavigate={handleNavigate}
          />
        );
      case 'entries':
        return (
          <TransactionsPage 
            userId={user.id}
            onNavigateToForm={handleNavigateToTransactionForm}
          />
        );
      case 'entries-form':
        return (
          <TransactionFormPage 
            userId={user.id}
            transactionId={editingTransactionId}
            mode={transactionFormMode}
            duplicateFromId={duplicateFromId}
            onBack={handleBackFromTransactionForm}
          />
        );
      
      // Secondary routes - Suspense for lazy loading
      case 'reports':
        return (
          <Suspense fallback={<GenericPageSkeleton />}>
            <ReportsPage userId={user.id} />
          </Suspense>
        );
      case 'budget':
        return (
          <Suspense fallback={<GenericPageSkeleton />}>
            <BudgetPage />
          </Suspense>
        );
      case 'scenarios':
        return (
          <Suspense fallback={<GenericPageSkeleton />}>
            <ScenariosPage 
              userId={user.id}
              onNavigateToForm={handleNavigateToScenarioForm}
              onNavigateToDetails={(scenarioId) => {
                // TODO: Navigate to scenario details
                console.log('Navigate to scenario details:', scenarioId);
              }}
            />
          </Suspense>
        );
      case 'scenarios-form':
        return (
          <Suspense fallback={<GenericPageSkeleton />}>
            <ScenarioFormPage 
              userId={user.id}
              scenarioId={editingScenarioId}
              onBack={handleBackFromScenarioForm}
            />
          </Suspense>
        );
      case 'sharing':
        return (
          <RestrictedRoute page="sharing">
            <Suspense fallback={<SharingSkeleton />}>
              <SharingPage userId={user.id} />
            </Suspense>
          </RestrictedRoute>
        );
      case 'settings':
        return (
          <RestrictedRoute page="settings">
            <Suspense fallback={<SettingsSkeleton />}>
              <SettingsPage />
            </Suspense>
          </RestrictedRoute>
        );
      case 'configurations':
        return (
          <RestrictedRoute page="configurations">
            <Suspense fallback={<GenericPageSkeleton />}>
              <ConfigurationsPage />
            </Suspense>
          </RestrictedRoute>
        );
      case 'reconciliation':
        return (
          <Suspense fallback={<GenericPageSkeleton />}>
            <ReconciliationPage />
          </Suspense>
        );
      case 'diagnostic':
        return <DatabaseDiagnostic />;
      default:
        return null;
    }
  };

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <SkipToMain />
          <AppLayout
          user={user}
          currentPath={currentPage === 'entries-form' ? '/entries' : `/${currentPage}`}
          breadcrumbs={getBreadcrumbs()}
          onNavigate={handleNavigate}
          onSignOut={handleSignOut}
        >
          <ErrorBoundary fallback={
            <div className="p-8 text-center" data-testid="page-error" role="alert">
              <h2>Erro ao carregar página</h2>
              <p className="text-muted-foreground mt-2">Tente recarregar ou voltar ao dashboard</p>
            </div>
          }>
            {renderPage()}
          </ErrorBoundary>
        </AppLayout>
        <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <CompanyProvider>
      <AppContent />
      <ErrorDebugPanel />
    </CompanyProvider>
  );
}
